import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight, AlertCircle, Upload, X, FileText, Image as ImageIcon, Video, Mic } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileUpload } from '../components/FileUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DiagnosisStart = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get('vehicleId');
  
  const [vehicle, setVehicle] = useState(null);
  const [symptomText, setSymptomText] = useState('');
  const [errorCodes, setErrorCodes] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [canUploadFiles, setCanUploadFiles] = useState(false);

  useEffect(() => {
    if (vehicleId) {
      fetchVehicle();
    }
    checkFileUploadFeature();
  }, [vehicleId]);

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/${vehicleId}`);
      setVehicle(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden des Fahrzeugs');
      navigate('/diagnosis/vehicle-select');
    }
  };

  const checkFileUploadFeature = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/my-subscription`);
      // FIX: API returns data.plan.features, not data.subscription.features
      const hasFileUploads = response.data?.plan?.features?.file_uploads || false;
      console.log('[DiagnosisStart] file_uploads feature:', hasFileUploads);
      console.log('[DiagnosisStart] Plan:', response.data?.plan?.name);
      console.log('[DiagnosisStart] All features:', response.data?.plan?.features);
      setCanUploadFiles(hasFileUploads);
    } catch (error) {
      console.error('[DiagnosisStart] Could not check file upload feature:', error);
      setCanUploadFiles(false);
    }
  };

  const handleFileUploaded = (uploadId) => {
    setUploadedFiles(prev => [...prev, uploadId]);
    toast.success('Datei erfolgreich hochgeladen');
  };

  const handleRemoveFile = (uploadId) => {
    setUploadedFiles(prev => prev.filter(id => id !== uploadId));
  };

  const handleStartDiagnosis = async () => {
    if (!symptomText.trim()) {
      toast.error('Bitte beschreiben Sie die Symptome');
      return;
    }

    setLoading(true);
    try {
      // Combine symptom text with error codes if provided
      let fullSymptomText = symptomText;
      if (errorCodes.trim()) {
        fullSymptomText += `\n\nFehlercodes: ${errorCodes}`;
      }

      const response = await axios.post(`${API}/sessions`, {
        vehicle_id: vehicleId,
        symptom_text: fullSymptomText,
        uploads: uploadedFiles
      });
      
      const sessionId = response.data.id;
      toast.success('Diagnose gestartet');
      navigate(`/diagnosis/session/${sessionId}`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Starten der Diagnose';
      toast.error(errorMsg);
      
      if (errorMsg.includes('subscription') || errorMsg.includes('limit')) {
        setTimeout(() => navigate('/subscription'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!vehicle) {
    return <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <p style={{ color: '#94A3B8' }}>Laden...</p>
    </div>;
  }

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          Diagnose starten
        </h2>

        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Ausgewähltes Fahrzeug</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg" style={{ color: '#E6EDF3' }}>
              {vehicle.brand} {vehicle.model} - {vehicle.engine} ({vehicle.year})
            </p>
          </CardContent>
        </Card>

        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle style={{ color: '#E6EDF3' }}>Symptome beschreiben</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Symptom Description */}
            <div>
              <Label style={{ color: '#E6EDF3' }}>Was ist das Problem?</Label>
              <Textarea
                value={symptomText}
                onChange={(e) => setSymptomText(e.target.value)}
                placeholder="Beschreiben Sie das Problem so detailliert wie möglich...\n\nBeispiel:\n- Motor startet nicht\n- Seltsame Geräusche beim Bremsen\n- Warnleuchte leuchtet\n- Leistungsverlust beim Beschleunigen"
                rows={8}
                className="mt-2"
                style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
              />
            </div>

            {/* Error Codes Input */}
            <div>
              <Label style={{ color: '#E6EDF3' }}>
                Fehlercodes (optional)
                <span className="text-sm text-gray-400 ml-2">z.B. P0300, P0171</span>
              </Label>
              <Input
                value={errorCodes}
                onChange={(e) => setErrorCodes(e.target.value)}
                placeholder="P0300, P0171, C1234..."
                className="mt-2"
                style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
              />
              <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                Wenn Sie ein OBD-Diagnosegerät haben, geben Sie hier die Fehlercodes ein. Die KI kann dann präzisere Diagnosen erstellen.
              </p>
            </div>

            {/* File Upload Section - Always visible */}
            <div className="space-y-3">
              <Label style={{ color: '#E6EDF3' }}>
                Fotos/Videos hochladen (optional)
                <span className="text-sm text-gray-400 ml-2">Zeigen Sie das Problem</span>
              </Label>
              
              {canUploadFiles ? (
                <>
                  <FileUpload 
                    sessionId={null}
                    onUploadComplete={handleFileUploaded}
                    acceptedTypes="image/*,video/*,audio/*"
                  />
                  
                  {uploadedFiles.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm" style={{ color: '#7CD4FD' }}>
                        {uploadedFiles.length} Datei(en) hochgeladen
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {uploadedFiles.map((fileId, index) => (
                          <div
                            key={fileId}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg"
                            style={{ backgroundColor: '#1F2937', borderColor: '#374151', border: '1px solid' }}
                          >
                            <FileText className="w-4 h-4" style={{ color: '#7CD4FD' }} />
                            <span className="text-sm" style={{ color: '#E6EDF3' }}>
                              Datei {index + 1}
                            </span>
                            <button
                              onClick={() => handleRemoveFile(fileId)}
                              className="ml-2 hover:bg-red-500/20 rounded p-1 transition-colors"
                            >
                              <X className="w-4 h-4" style={{ color: '#EF4444' }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="p-4 rounded-lg" style={{ backgroundColor: '#F59E0B20', borderColor: '#F59E0B', border: '1px solid' }}>
                  <div className="flex items-start gap-2">
                    <Upload className="w-5 h-5 mt-0.5" style={{ color: '#F59E0B' }} />
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#F59E0B' }}>
                        Premium-Feature
                      </p>
                      <p className="text-sm mt-1" style={{ color: '#E6EDF3' }}>
                        Datei-Upload ist nur in Premium-Plänen verfügbar. 
                        <button 
                          onClick={() => window.location.href = '/subscription'}
                          className="ml-1 underline hover:text-white transition-colors"
                        >
                          Jetzt upgraden
                        </button>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-2 p-4 rounded-lg" style={{ backgroundColor: '#7CD4FD20', borderColor: '#7CD4FD', border: '1px solid' }}>
              <AlertCircle className="w-5 h-5 mt-0.5" style={{ color: '#7CD4FD' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: '#7CD4FD' }}>Tipp für beste Ergebnisse:</p>
                <ul className="text-sm mt-2 space-y-1" style={{ color: '#E6EDF3' }}>
                  <li>• Beschreiben Sie das Problem so detailliert wie möglich</li>
                  <li>• Geben Sie Fehlercodes an, falls vorhanden (z.B. P0300, P0171)</li>
                  {canUploadFiles && <li>• Laden Sie Fotos/Videos hoch, um das Problem zu zeigen</li>}
                  <li>• Erwähnen Sie, wann und wie oft das Problem auftritt</li>
                </ul>
              </div>
            </div>

            {/* Start Button */}
            <Button
              onClick={handleStartDiagnosis}
              disabled={loading || !symptomText.trim()}
              className="w-full py-6 text-lg font-semibold"
              style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
            >
              {loading ? 'Starte Diagnose...' : 'Diagnose starten'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};